import { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, getDocs, where, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { Message } from '../../models/types';
import { Send, X } from 'lucide-react';
import { toast } from 'react-toastify';
import ChatMessage from './ChatMessage';

interface ChatModalProps {
  projectId: string;
  chatId: string;
  otherUserId: string;
  otherUserName: string;
  onClose: () => void;
  allowMarketplaceChat?: boolean; // New prop to allow marketplace chat
}

const ChatModal = ({ projectId, chatId, otherUserId, otherUserName, onClose, allowMarketplaceChat = false }: ChatModalProps) => {
  const { currentUser, userData } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  useEffect(() => {
    if (!chatId || !currentUser) return;

    // Check if user has permission to access this chat
    const checkPermission = async () => {
      try {
        // Get project data
        const projectRef = doc(db, 'projects', projectId);
        const projectSnap = await getDoc(projectRef);
        
        if (!projectSnap.exists()) {
          throw new Error('Project not found');
        }

        const projectData = projectSnap.data();

        // Check if user is project owner
        if (projectData.userId === currentUser.uid) {
          return true;
        }

        // For marketplace chat, allow providers to initiate chat
        if (allowMarketplaceChat && userData?.role === 'provider') {
          return true;
        }

        // Check if user has an accepted proposal
        const proposalsRef = collection(db, 'proposals');
        const q = query(
          proposalsRef,
          where('projectId', '==', projectId),
          where('providerId', '==', currentUser.uid),
          where('status', '==', 'accepted')
        );
        
        const proposalSnap = await getDocs(q);
        return !proposalSnap.empty;
      } catch (error) {
        console.error('Error checking permissions:', error);
        return false;
      }
    };

    const initializeChat = async () => {
      try {
        const hasPermission = await checkPermission();
        
        if (!hasPermission) {
          toast.error('You do not have permission to access this chat');
          onClose();
          return;
        }

        // Initialize chat document if it doesn't exist
        const chatRef = doc(db, `projects/${projectId}/chats/${chatId}`);
        const chatSnap = await getDoc(chatRef);

        if (!chatSnap.exists()) {
          await setDoc(chatRef, {
            participants: {
              [currentUser.uid]: true,
              [otherUserId]: true
            },
            createdAt: serverTimestamp(),
            lastMessageAt: serverTimestamp(),
            unreadCount: {
              [currentUser.uid]: 0,
              [otherUserId]: 0
            }
          });
        }

        // Subscribe to messages
        const messagesRef = collection(db, `projects/${projectId}/chats/${chatId}/messages`);
        const q = query(messagesRef, orderBy('createdAt', 'asc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const messageList: Message[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            messageList.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date()
            } as Message);

            // Mark messages as read if we're the receiver
            if (data.receiverId === currentUser.uid && !data.readStatus) {
              updateDoc(doc.ref, { readStatus: true }).catch(console.error);
            }
          });
          setMessages(messageList);
          
          if (isAtBottom) {
            scrollToBottom();
          }
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error initializing chat:', error);
        toast.error('Error initializing chat');
        onClose();
      }
    };

    const unsubscribe = initializeChat();
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [chatId, projectId, currentUser, isAtBottom, onClose, otherUserId, allowMarketplaceChat, userData?.role]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
      setIsAtBottom(isBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const validateMessage = (message: string): boolean => {
    if (!message.trim()) {
      setError('Message cannot be empty');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !validateMessage(newMessage)) return;

    try {
      setSending(true);
      
      // Add message
      const messageData = {
        chatId,
        senderId: currentUser.uid,
        receiverId: otherUserId,
        content: newMessage.trim(),
        createdAt: serverTimestamp(),
        readStatus: false
      };

      await addDoc(collection(db, `projects/${projectId}/chats/${chatId}/messages`), messageData);

      // Update chat's last message
      await updateDoc(doc(db, `projects/${projectId}/chats/${chatId}`), {
        lastMessage: newMessage.trim(),
        lastMessageAt: serverTimestamp(),
        [`unreadCount.${otherUserId}`]: (messages.filter(m => !m.readStatus && m.senderId === currentUser.uid).length || 0) + 1
      });

      setNewMessage('');
      setError(null);
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      if (error instanceof Error && error.message.includes('permission-denied')) {
        toast.error('You do not have permission to send messages in this chat');
      } else {
        toast.error('Error sending message');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Chat with {otherUserName}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div ref={chatContainerRef} className="h-96 p-4 overflow-y-auto">
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="p-4 border-t">
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="Type your message..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600"
                disabled={sending}
              />
              <button
                type="submit"
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={sending || !newMessage.trim()}
              >
                <Send className="h-4 w-4" />
                Send
              </button>
            </div>
            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatModal;